// Brand pass over Mapbox Standard, the one base map on every surface
// (packages/config/map.ts). Standard keeps its own layers inside the
// "basemap" import, so the brand is set through its config — colours and
// switches — not by editing layers. Day and night are its light presets: a
// theme or time-of-day switch is a config change, never a style reload.
// Applied on load and on every styledata; idempotent.

import type { Map as MapboxMap } from "mapbox-gl";

// Brand primary — vault 03-Design/BRANDING_v2.md (tokens.ts was retired with
// the shadcn migration; the canonical CSS value lives in styles/globals.css).
const BRAND_PRIMARY = "#00927a";

const BASEMAP = "basemap";

/**
 * Brand colours and switches for Standard. Land use in separate colours
 * (parks, schools, hospitals, commercial, industrial) with water leaning
 * teal-blue; no POI, transit, landmark-icon or admin-boundary clutter; lit 3D
 * buildings. Standard derives its night colours from these through the
 * night light preset.
 */
const BRAND_BASEMAP = {
  showPointOfInterestLabels: false,
  showTransitLabels: false,
  showLandmarkIcons: false,
  showAdminBoundaries: false,
  show3dObjects: true,
  colorLand: "hsl(40, 25%, 96%)",
  colorWater: "hsl(192, 68%, 76%)",
  colorGreenspace: "hsl(130, 45%, 79%)",
  colorCommercial: "hsl(28, 90%, 91%)",
  colorEducation: "hsl(45, 85%, 87%)",
  colorMedical: "hsl(355, 75%, 92%)",
  colorIndustrial: "hsl(222, 28%, 89%)",
};

/** The admin ops map: the same base, read from straight above, so no 3D.
 *  Place and street names stay ON — hiding them left areas with no names at
 *  all (Leon, 2026-10-22). */
const OPERATIONS_BASEMAP = {
  show3dObjects: false,
};

/** The rider's drive map: flat building footprints. At the street-level POV
 *  the 3D blocks stood between the rider and his own route line, hiding the
 *  road ahead — the big nav apps flatten them while driving for the same
 *  reason. Every other map keeps them. */
const NAVIGATION_BASEMAP = {
  show3dObjects: false,
};

/** Which config a surface gets. `rider-navigation` also unlocks the steeper
 *  drive camera (ServiceMap's maxPitch); `operations` is the admin ops map. */
export type MapPresentation = "default" | "rider-navigation" | "operations";

export interface GeoPointLike {
  lat: number;
  lng: number;
}

/** Minimal GeoJSON polygon feature — avoids a @types/geojson dependency. */
export interface AreaFeature {
  type: "Feature";
  properties: Record<string, unknown>;
  geometry: { type: "Polygon"; coordinates: [number, number][][] };
}

/** A circle (polygon) around `center` of `radiusKm` — the coverage ring. */
export function circlePolygon(center: GeoPointLike, radiusKm: number, points = 96): AreaFeature {
  const latDelta = radiusKm / 110.574;
  const lngDelta = radiusKm / (111.32 * Math.cos((center.lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= points; i++) {
    const theta = (i / points) * 2 * Math.PI;
    ring.push([center.lng + lngDelta * Math.cos(theta), center.lat + latDelta * Math.sin(theta)]);
  }
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [ring] },
  };
}

/** Set a property without letting one style-version mismatch break the pass. */
function trySet(fn: () => void): void {
  try {
    fn();
  } catch {
    // Layer/property absent in this style version — skip, never throw.
  }
}

/** Paint property that keeps a layer type its own colour under Standard's
 *  lighting. Without it our routes, rings and dots go near-black at night. */
const EMISSIVE: Partial<Record<string, string>> = {
  line: "line-emissive-strength",
  fill: "fill-emissive-strength",
  circle: "circle-emissive-strength",
  symbol: "text-emissive-strength",
  "fill-extrusion": "fill-extrusion-emissive-strength",
};

/** Standard's config for a surface — passed at map creation (so the first
 *  frame is already ours, no flash of stock colours) and re-applied by
 *  applyBrandStyle when day/night changes. */
export function brandBasemapConfig(dark: boolean, presentation: MapPresentation = "default") {
  return {
    ...BRAND_BASEMAP,
    ...(presentation === "operations" ? OPERATIONS_BASEMAP : {}),
    ...(presentation === "rider-navigation" ? NAVIGATION_BASEMAP : {}),
    lightPreset: dark ? "night" : "day",
  };
}

/** What each map last had applied, so styledata re-runs skip the config. */
const applied = new WeakMap<MapboxMap, string>();

/**
 * Idempotent — safe to call on every `styledata`. Sets the brand config and
 * light preset (only when they changed), then makes every one of OUR layers
 * — Standard's own live inside the import, so `getStyle().layers` lists only
 * ours — ignore the lighting. Layers added later are caught on the next
 * styledata. A config key a future Standard drops is ignored by Mapbox.
 */
export function applyBrandStyle(
  map: MapboxMap,
  dark: boolean,
  presentation: MapPresentation = "default",
): void {
  // No isStyleLoaded() gate: with Standard that stays false until every
  // first tile has loaded (10 s+), and the brand must not wait for tiles.
  const key = `${presentation}:${dark ? "night" : "day"}`;
  if (applied.get(map) !== key) {
    try {
      map.setConfig(BASEMAP, brandBasemapConfig(dark, presentation));
      applied.set(map, key);
    } catch {
      // The basemap import is not in yet — the next styledata retries.
    }
  }
  let layers: { id: string; type: string }[] = [];
  try {
    layers = map.getStyle()?.layers ?? [];
  } catch {
    return; // style not parsed yet; styledata calls again
  }
  // The property name is picked per layer type at runtime, which Mapbox's
  // per-name paint typings cannot follow.
  const paint = map as unknown as LoosePaint;
  for (const layer of layers) {
    const prop = EMISSIVE[layer.type];
    if (!prop || paint.getPaintProperty(layer.id, prop) === 1) continue;
    trySet(() => paint.setPaintProperty(layer.id, prop, 1));
    if (layer.type === "symbol") {
      trySet(() => paint.setPaintProperty(layer.id, "icon-emissive-strength", 1));
    }
  }
}

interface LoosePaint {
  getPaintProperty(layerId: string, name: string): unknown;
  setPaintProperty(layerId: string, name: string, value: unknown): void;
}

const AREA_SOURCE = "bnb-service-area";

/**
 * Teal service-area outline (fill + dashed line). Idempotent, piece by piece,
 * so a call that lands before the style is parsed simply retries on the next
 * styledata. Not gated on isStyleLoaded(): with Standard that waits for every
 * first tile, and the ring must not.
 */
export function addServiceAreaLayers(map: MapboxMap, area: AreaFeature): void {
  try {
    if (!map.getSource(AREA_SOURCE)) {
      map.addSource(AREA_SOURCE, { type: "geojson", data: area as never });
    }
    if (!map.getLayer(`${AREA_SOURCE}-fill`)) {
      map.addLayer({
        id: `${AREA_SOURCE}-fill`,
        type: "fill",
        source: AREA_SOURCE,
        paint: { "fill-color": BRAND_PRIMARY, "fill-opacity": 0.06 },
      });
    }
    if (!map.getLayer(`${AREA_SOURCE}-line`)) {
      map.addLayer({
        id: `${AREA_SOURCE}-line`,
        type: "line",
        source: AREA_SOURCE,
        paint: {
          "line-color": BRAND_PRIMARY,
          "line-width": 1.5,
          "line-opacity": 0.45,
          "line-dasharray": [2, 2],
        },
      });
    }
  } catch {
    // Style not parsed yet — the next styledata retries.
  }
}
